import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import axios from 'axios';

/**
 * Herkese açık başvuru sayfası — /kariyer/:slug
 *
 * LinkedIn ilanındaki "Harici bir web site üzerinden başvuru alın" adresi budur.
 * İlan bilgisi CRM'deki işe alım kaydından gelir; gönderilen başvuru aynı kaydın
 * aday listesine düşer ve AI ön elemesine girer. Giriş gerektirmez.
 */

const API = `${process.env.REACT_APP_API_URL}/hr/public`;

const BRAND = '#ed1c2a';
const BRAND_DARK = '#c41520';
const BRAND_LIGHT = '#fdebed';
const DARK = '#1a1a2e';
const MAX_FILE_SIZE = 12 * 1024 * 1024;

interface PublicRecruitment {
  slug: string;
  title: string;
  description: string;
  location: string | null;
  closes_at: string | null;
  accepting: boolean;
  closed_reason: string | null;
  company: string;
  confirmation_email?: boolean;
  max_file_size: number;
  accepted_types: string[];
}

interface FormState {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  linkedin_url: string;
  message: string;
  consent: boolean;
  website: string; // honeypot — gerçek kullanıcı görmez
}

const EMPTY_FORM: FormState = { first_name: '', last_name: '', email: '', phone: '', linkedin_url: '', message: '', consent: false, website: '' };

// Türkçe büyük harf: 'i' → 'İ' (CSS text-transform 'I' üretir)
const upperTr = (text: string) => text.toLocaleUpperCase('tr-TR');

const formatDate = (iso: string) => new Date(iso).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
const formatSize = (bytes: number) => (bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`);

const isAllowedFile = (file: File) => {
  const name = file.name.toLowerCase();
  return name.endsWith('.pdf') || name.endsWith('.docx');
};

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    minHeight: '100vh',
    background: `linear-gradient(160deg, ${DARK} 0%, #2d1b30 40%, #3a1520 100%)`,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '32px 16px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
    boxSizing: 'border-box' as const,
  },
  container: {
    background: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.05)',
    width: '100%',
    maxWidth: '720px',
    overflow: 'hidden',
  },
  header: {
    background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
    padding: '28px 32px 24px',
    color: '#ffffff',
    position: 'relative' as const,
    overflow: 'hidden',
  },
  headerDecor: { position: 'absolute' as const, top: '-30px', right: '-30px', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)' },
  eyebrow: { fontSize: '0.75rem', letterSpacing: '0.1em', color: '#ffffff', opacity: 0.85, margin: 0, fontWeight: 600 },
  title: { fontSize: '1.5rem', fontWeight: 700, color: '#ffffff', margin: '6px 0 4px', letterSpacing: '-0.01em', position: 'relative' as const, zIndex: 1 },
  meta: { fontSize: '0.85rem', color: '#ffffff', opacity: 0.9, margin: 0, display: 'flex', gap: '14px', flexWrap: 'wrap' as const },
  body: { padding: '28px 32px 32px' },
  section: { marginBottom: '26px' },
  sectionTitle: { fontSize: '0.8rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.08em', margin: '0 0 12px' },
  description: { whiteSpace: 'pre-wrap' as const, color: '#334155', fontSize: '0.95rem', lineHeight: 1.6, margin: 0 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px' },
  label: { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#334155', marginBottom: '6px' },
  input: {
    width: '100%',
    padding: '11px 13px',
    border: '1.5px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '0.95rem',
    color: '#0f172a',
    outline: 'none',
    boxSizing: 'border-box' as const,
    background: '#fff',
    fontFamily: 'inherit',
  },
  dropzone: {
    border: '2px dashed #cbd5e1',
    borderRadius: '12px',
    padding: '22px 16px',
    textAlign: 'center' as const,
    cursor: 'pointer',
    background: '#f8fafc',
    transition: 'all .15s',
  },
  consentBox: { display: 'flex', gap: '10px', alignItems: 'flex-start', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 14px', fontSize: '0.82rem', color: '#475569', lineHeight: 1.5 },
  btnPrimary: {
    width: '100%',
    padding: '14px 20px',
    background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 8px 20px rgba(237,28,42,0.25)',
  },
  alertError: { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: '10px', padding: '12px 14px', fontSize: '0.9rem', marginBottom: '16px' },
  alertInfo: { background: BRAND_LIGHT, border: `1px solid ${BRAND}33`, color: BRAND_DARK, borderRadius: '10px', padding: '12px 14px', fontSize: '0.9rem' },
  poweredBy: { textAlign: 'center' as const, padding: '16px', borderTop: '1px solid #f1f5f9', fontSize: '0.75rem', color: '#a0aec0' },
  centered: { textAlign: 'center' as const, padding: '48px 24px' },
};

const CareerApplyPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const src = useMemo(() => new URLSearchParams(location.search).get('src') || '', [location.search]);

  const [job, setJob] = useState<PublicRecruitment | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ reference: string } | null>(null);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get<PublicRecruitment>(`${API}/${encodeURIComponent(slug)}`)
      .then((res) => {
        if (cancelled) return;
        setJob(res.data);
        document.title = `${res.data.title} | Kariyer | ${res.data.company}`;
      })
      .catch(() => !cancelled && setNotFound(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const update = (key: keyof FormState, value: string | boolean) => setForm((prev) => ({ ...prev, [key]: value }));

  const pickFile = useCallback((f: File | null) => {
    setFileError('');
    if (!f) return setFile(null);
    if (!isAllowedFile(f)) {
      setFile(null);
      return setFileError('Yalnızca PDF veya DOCX dosyası yükleyebilirsiniz.');
    }
    if (f.size > MAX_FILE_SIZE) {
      setFile(null);
      return setFileError('Dosya 12 MB sınırını aşıyor.');
    }
    setFile(f);
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    pickFile(e.dataTransfer.files && e.dataTransfer.files[0] ? e.dataTransfer.files[0] : null);
  };

  const validate = (): string => {
    if (form.first_name.trim().length < 2) return 'Adınızı yazın.';
    if (form.last_name.trim().length < 2) return 'Soyadınızı yazın.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) return 'Geçerli bir e-posta adresi yazın.';
    if (form.phone.trim() && form.phone.replace(/\D/g, '').length < 7) return 'Geçerli bir telefon numarası yazın.';
    if (form.linkedin_url.trim() && !/^https?:\/\/([a-z0-9-]+\.)*linkedin\.com\//i.test(form.linkedin_url.trim())) return 'LinkedIn adresi https://www.linkedin.com/in/... biçiminde olmalı.';
    if (!file) return 'CV dosyanızı ekleyin (PDF veya DOCX).';
    if (!form.consent) return 'Devam etmek için aydınlatma metnini onaylamanız gerekir.';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !job) return;
    const v = validate();
    if (v) {
      setError(v);
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('first_name', form.first_name.trim());
      fd.append('last_name', form.last_name.trim());
      fd.append('email', form.email.trim());
      if (form.phone.trim()) fd.append('phone', form.phone.trim());
      if (form.linkedin_url.trim()) fd.append('linkedin_url', form.linkedin_url.trim());
      if (form.message.trim()) fd.append('message', form.message.trim());
      fd.append('consent', 'true');
      fd.append('website', form.website);
      if (src) fd.append('src', src);
      fd.append('cv', file as File);
      const res = await axios.post(`${API}/${encodeURIComponent(slug)}/apply`, fd, { timeout: 120000 });
      setResult({ reference: res.data.reference });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message;
      if (status === 429) setError('Çok fazla deneme yapıldı. Lütfen 15 dakika sonra tekrar deneyin.');
      else setError(msg || 'Başvuru gönderilemedi. Lütfen bağlantınızı kontrol edip tekrar deneyin.');
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } finally {
      setSubmitting(false);
    }
  };

  const descriptionLong = (job?.description || '').length > 700;
  const descriptionShown = !descriptionLong || showFullDescription ? job?.description || '' : `${(job?.description || '').slice(0, 700).trimEnd()}…`;

  const renderShell = (children: React.ReactNode, headerContent?: React.ReactNode) => (
    <div style={styles.wrapper} lang="tr">
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.headerDecor} />
          {headerContent}
        </div>
        {children}
        <div style={styles.poweredBy}>
          <a href="https://www.aynetteknoloji.com.tr" target="_blank" rel="noopener noreferrer" style={{ color: BRAND, fontWeight: 600, textDecoration: 'none' }}>
            Aynet Teknoloji
          </a>{' '}
          · Kariyer
        </div>
      </div>
    </div>
  );

  if (loading) {
    return renderShell(
      <div style={styles.centered}>
        <div style={{ width: 36, height: 36, border: `3px solid ${BRAND_LIGHT}`, borderTopColor: BRAND, borderRadius: '50%', margin: '0 auto 14px', animation: 'spin .8s linear infinite' }} />
        <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        <div style={{ color: '#64748b' }}>İlan yükleniyor…</div>
      </div>,
      <p style={styles.eyebrow}>KARİYER</p>,
    );
  }

  if (notFound || !job) {
    return renderShell(
      <div style={styles.centered}>
        <div style={{ fontSize: '2.4rem', marginBottom: 10 }}>🔎</div>
        <h2 style={{ margin: '0 0 8px', color: '#0f172a' }}>İlan bulunamadı</h2>
        <p style={{ color: '#64748b', margin: 0 }}>Bağlantı hatalı ya da ilan kaldırılmış olabilir.</p>
      </div>,
      <>
        <p style={styles.eyebrow}>KARİYER</p>
        <h1 style={styles.title}>Aynet Teknoloji</h1>
      </>,
    );
  }

  const header = (
    <>
      <p style={styles.eyebrow}>{upperTr(job.company)} · KARİYER</p>
      <h1 style={styles.title}>{job.title}</h1>
      <p style={styles.meta}>
        {job.location && <span>📍 {job.location}</span>}
        {job.closes_at && <span>🗓 Son başvuru: {formatDate(job.closes_at)}</span>}
      </p>
    </>
  );

  if (result) {
    return renderShell(
      <div style={styles.centered}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#dcfce7', color: '#15803d', fontSize: '2rem', lineHeight: '64px', margin: '0 auto 16px' }}>✓</div>
        <h2 style={{ margin: '0 0 8px', color: '#0f172a' }}>Başvurunuz alındı</h2>
        <p style={{ color: '#475569', margin: '0 0 18px', lineHeight: 1.6 }}>
          <strong>{job.title}</strong> pozisyonu için başvurunuz İnsan Kaynakları ekibimize iletildi. Profiliniz pozisyonla eşleşirse sizinle e-posta ya da telefon yoluyla iletişime geçeceğiz.
        </p>
        <div style={{ ...styles.alertInfo, display: 'inline-block' }}>
          Başvuru referansınız: <strong style={{ letterSpacing: '0.04em' }}>{result.reference}</strong>
        </div>
      </div>,
      header,
    );
  }

  if (!job.accepting) {
    return renderShell(
      <div style={styles.centered}>
        <div style={{ fontSize: '2.4rem', marginBottom: 10 }}>⏸</div>
        <h2 style={{ margin: '0 0 8px', color: '#0f172a' }}>Başvurular kapalı</h2>
        <p style={{ color: '#64748b', margin: 0 }}>{job.closed_reason || 'Bu ilan artık başvuru kabul etmiyor.'}</p>
      </div>,
      header,
    );
  }

  return renderShell(
    <div style={styles.body}>
      <div style={styles.section}>
        <p style={styles.sectionTitle}>İLAN DETAYI</p>
        <p style={styles.description}>{descriptionShown}</p>
        {descriptionLong && (
          <button type="button" onClick={() => setShowFullDescription((v) => !v)} style={{ background: 'none', border: 'none', color: BRAND, fontWeight: 600, cursor: 'pointer', padding: '8px 0 0', fontSize: '0.9rem' }}>
            {showFullDescription ? 'Daha az göster' : 'Devamını göster'}
          </button>
        )}
      </div>

      <div ref={formRef} style={styles.section}>
        <p style={styles.sectionTitle}>BAŞVURU FORMU</p>
        {error && <div style={styles.alertError}>{error}</div>}
        <form onSubmit={handleSubmit} noValidate>
          <div style={styles.grid}>
            <div>
              <label style={styles.label} htmlFor="first_name">Ad *</label>
              <input id="first_name" style={styles.input} value={form.first_name} onChange={(e) => update('first_name', e.target.value)} autoComplete="given-name" maxLength={120} />
            </div>
            <div>
              <label style={styles.label} htmlFor="last_name">Soyad *</label>
              <input id="last_name" style={styles.input} value={form.last_name} onChange={(e) => update('last_name', e.target.value)} autoComplete="family-name" maxLength={120} />
            </div>
            <div>
              <label style={styles.label} htmlFor="email">E-posta *</label>
              <input id="email" type="email" style={styles.input} value={form.email} onChange={(e) => update('email', e.target.value)} autoComplete="email" maxLength={255} />
            </div>
            <div>
              <label style={styles.label} htmlFor="phone">Telefon</label>
              <input id="phone" type="tel" style={styles.input} value={form.phone} onChange={(e) => update('phone', e.target.value)} autoComplete="tel" placeholder="+90 5xx xxx xx xx" maxLength={60} />
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <label style={styles.label} htmlFor="linkedin_url">LinkedIn profili</label>
            <input id="linkedin_url" type="url" style={styles.input} value={form.linkedin_url} onChange={(e) => update('linkedin_url', e.target.value)} placeholder="https://www.linkedin.com/in/kullanici-adi" maxLength={500} />
          </div>

          <div style={{ marginTop: 14 }}>
            <label style={styles.label} htmlFor="message">Kısa not <span style={{ color: '#94a3b8', fontWeight: 400 }}>(isteğe bağlı)</span></label>
            <textarea id="message" style={{ ...styles.input, minHeight: 90, resize: 'vertical' }} value={form.message} onChange={(e) => update('message', e.target.value)} placeholder="Bu pozisyon için sizi öne çıkaran birkaç cümle…" maxLength={2000} />
          </div>

          {/* Honeypot — insanlar görmez, botlar doldurur */}
          <div style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
            <label htmlFor="website">Web sitesi</label>
            <input id="website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => update('website', e.target.value)} />
          </div>

          <div style={{ marginTop: 14 }}>
            <label style={styles.label}>CV dosyası * <span style={{ color: '#94a3b8', fontWeight: 400 }}>(PDF veya DOCX, en fazla 12 MB)</span></label>
            <div
              style={{ ...styles.dropzone, borderColor: dragActive ? BRAND : file ? '#86efac' : '#cbd5e1', background: dragActive ? BRAND_LIGHT : file ? '#f0fdf4' : '#f8fafc' }}
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
            >
              <input ref={inputRef} type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" style={{ display: 'none' }} onChange={(e) => pickFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
              {file ? (
                <div>
                  <div style={{ fontWeight: 600, color: '#166534' }}>📄 {file.name}</div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 4 }}>
                    {formatSize(file.size)} ·{' '}
                    <span
                      style={{ color: BRAND, cursor: 'pointer', fontWeight: 600 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        pickFile(null);
                        if (inputRef.current) inputRef.current.value = '';
                      }}
                    >
                      Değiştir
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '1.6rem', marginBottom: 4 }}>📎</div>
                  <div style={{ fontWeight: 600, color: '#334155' }}>CV'nizi buraya bırakın ya da seçmek için tıklayın</div>
                </div>
              )}
            </div>
            {fileError && <div style={{ color: '#b91c1c', fontSize: '0.82rem', marginTop: 6 }}>{fileError}</div>}
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={styles.consentBox}>
              <input type="checkbox" checked={form.consent} onChange={(e) => update('consent', e.target.checked)} style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0 }} />
              <span>
                Paylaştığım kişisel verilerin ve CV'min, <strong>{job.company}</strong> tarafından yalnızca bu işe alım sürecinin yürütülmesi amacıyla, 6698 sayılı Kişisel Verilerin Korunması Kanunu kapsamında işlenmesini ve değerlendirme süresince saklanmasını kabul ediyorum.
              </span>
            </label>
          </div>

          <div style={{ marginTop: 20 }}>
            <button type="submit" style={{ ...styles.btnPrimary, opacity: submitting ? 0.7 : 1, cursor: submitting ? 'wait' : 'pointer' }} disabled={submitting}>
              {submitting ? 'Gönderiliyor…' : 'Başvuruyu Gönder'}
            </button>
            {job.confirmation_email !== false && (
              <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.78rem', margin: '10px 0 0' }}>Başvurunuz alındığında e-posta ile teyit gönderilir.</p>
            )}
          </div>
        </form>
      </div>
    </div>,
    header,
  );
};

export default CareerApplyPage;
